import { BuildServiceUrlsArgs } from '../../types';
import { isConnectionIdValid } from '../utils';
import * as Constants from '../../utils/constants';

/**
 * Builds URLs for rendering a framed service and opening a service
 * page in a seprate window outside of Teams. URLs are built based
 * on project, service and connection information which was gathered
 * by parsing Teams' entityId or by selecting a service during tab configuration
 *
 * @param {BuildServiceUrlsArgs} args
 * @returns
 * embedUrl: framed service URL; linkToUrl: service page URL (for use outside of Teams)
 */
export async function buildServiceUrls(
  args: BuildServiceUrlsArgs
): Promise<{ embedUrl: string; linkToUrl: string }> {
  return buildServiceUrlsFromTemplate({
    ...(await getServiceUrlComponents(args)),
    projectId: args.projectId,
  });
}

type ServiceUrlComponents = {
  moddedTemplate: string;
  baseUrl?: string;
  queryParams: string;
  urlFragment: string;
};

const getServiceUrlComponents = async ({
  service,
  connectionId,
  defaultRoute,
  subEntityId,
  iModel,
}: BuildServiceUrlsArgs): Promise<ServiceUrlComponents> => {
  let moddedTemplate = service.urlTemplate;
  let queryParams = 'auth-via-popup=true';
  let urlFragment = '';

  switch (service.id) {
    case Constants.ServiceRegistry.projectInsightsId:
      moddedTemplate = '{0}/PBI.Frontend/en-US/{1}';
      break;
    case Constants.ServiceRegistry.deliverablesManagementId:
      moddedTemplate = `${service.urlTemplate.replace(
        'gate',
        '{1}/Projects'
      )}/PackageList`;
      queryParams = 'embeddedin=pwteams';
      urlFragment = '#incoming';
      break;
    case Constants.ServiceRegistry.projectWiseWebConnectionsId:
      moddedTemplate +=
        connectionId && isConnectionIdValid(connectionId)
          ? `/${connectionId}`
          : '';
      break;
    case Constants.ServiceRegistry.projectWise365Id:
      if (subEntityId || defaultRoute) {
        moddedTemplate += subEntityId
          ? `/documents/documents-connections/v2/${subEntityId}`
          : `/${defaultRoute}`;
      }
      queryParams = 'isEmbedded=teams';
      break;
    case Constants.ServiceRegistry.designReviewId:
      moddedTemplate +=
        `/imodeljs/?projectId={1}&iModelId=${iModel?.iModelId}` +
        `&changeSetId=${iModel?.namedVersionId}` +
        `&shareViewId=${iModel?.defaultViewId}`;
      queryParams = '';
      break;
  }

  return {
    moddedTemplate,
    baseUrl: service.buddiUrl,
    queryParams,
    urlFragment,
  };
};

const buildServiceUrlsFromTemplate = ({
  moddedTemplate,
  baseUrl,
  projectId,
  queryParams,
  urlFragment,
}: ServiceUrlComponents & { projectId: string }) => {
  const linkToUrl = `${moddedTemplate
    .replace(/\{0\}/, baseUrl!)
    .replace(/\{1\}/g, projectId)}`;

  const joiner = containsQueryParam(linkToUrl) ? '&' : '?';
  const embedUrl = `${linkToUrl}${joiner}${queryParams}`;

  return {
    embedUrl: `${embedUrl}${urlFragment}`,
    linkToUrl: `${linkToUrl}${urlFragment}`,
  };
};

const containsQueryParam = (url: string): boolean => {
  const pattern = new RegExp(/\?+/g);
  return pattern.test(url);
};
